import { RowSelectionState } from "@tanstack/react-table";
import { isAudit } from "PFApp/activities/helpers";
import { BookingsToReassignSelector } from "PFApp/booking/components/reassign_booking_modal/bookings_to_reassign_selector";
import { ReassignSelectOption } from "PFApp/booking/components/reassign_booking_modal/bookings_to_reassign_selector.types";
import { FiltersV2StoreContextProvider } from "PFApp/components/filters_v2";
import { FiltersV2StorePage } from "PFApp/components/filters_v2/stores/filters_v2_store/filters_v2_store.types";
import { ProfileSource, ProfileSourceToggle } from "PFApp/components/profile_source_toggle";
import { useTemplateFind } from "PFApp/hooks";
import { Alert } from "PFComponents/alert";
import { Stack } from "PFComponents/containers";
import { Activity, Booking } from "PFTypes";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import css from "./reassign_content.module.scss";
import { ReassignContentMatches } from "./reassign_content_matches";
import { ReassignContentNamedResources } from "./reassign_content_named_resources";
import { ReassignLabel } from "./reassign_label";
import { SelectedBookings } from "./selected_bookings";

export type RowSelection = {
  onTargetProfileChange: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  rowSelectionState: RowSelectionState;
};

type ReassignContentProps = {
  bookings: Booking[];
  notAllowedBookings: Booking[];
  activities: Activity[];
  notAllowedActivities: Activity[];
  sourceProfileIds: number[];
  reassignOption: ReassignSelectOption;
  setReassignOption: React.Dispatch<React.SetStateAction<ReassignSelectOption>>;
  allowedTargets: ProfileSource[];
  ignoredBookingsCount: number;
  selectedTab?: ProfileSource;
  setSelectedTab: (value: ProfileSource) => void;
} & RowSelection;

export const ReassignContent = ({
  bookings,
  notAllowedBookings,
  activities,
  notAllowedActivities,
  sourceProfileIds,
  onTargetProfileChange,
  rowSelectionState,
  reassignOption,
  setReassignOption,
  allowedTargets,
  ignoredBookingsCount,
  selectedTab,
  setSelectedTab
}: ReassignContentProps) => {
  const { t } = useTranslation(["bookingModule", "activities", "translation"]);

  const findTemplate = useTemplateFind();

  useEffect(() => {
    onTargetProfileChange({});
  }, [selectedTab]);

  const isSingleBookingReassignment = bookings.length === 1;
  const isMultipleBookingsSelected = bookings.length + notAllowedBookings.length > 1;
  const isNamedResourceTabVisible = allowedTargets.includes(ProfileSource.NamedResource);

  const existAuditRoleActivities = activities.some((activity) =>
    isAudit(findTemplate({ id: activity.template_id }))
  );

  return (
    <Stack gap="spacingLg">
      {ignoredBookingsCount > 0 && (
        <Alert
          kind="warning"
          title={t("bookingModule:bookings.reassign.sidePanel.selectedBookingsCanNotBeReassigned", {
            count: ignoredBookingsCount
          })}
        />
      )}
      {isMultipleBookingsSelected && (
        <SelectedBookings
          bookings={bookings}
          notAllowedBookings={notAllowedBookings}
          activities={activities}
          notAllowedActivities={notAllowedActivities}
        />
      )}
      <Stack gap="spacingLg" className={css.stickyContainer}>
        {isSingleBookingReassignment && !!bookings[0]?.activity_id && !existAuditRoleActivities && (
          <BookingsToReassignSelector
            activityId={bookings[0].activity_id}
            profileId={bookings[0].profile_id}
            currentBooking={{
              id: bookings[0].id,
              startDate: bookings[0].start_date,
              endDate: bookings[0].end_date
            }}
            reassignOptionSelected={reassignOption}
            setReassignOptionSelected={setReassignOption}
            dividerEnabled
          />
        )}
        <ReassignLabel sourceProfileIds={sourceProfileIds} />
        <ProfileSourceToggle
          value={selectedTab ?? null}
          onChange={(id) => setSelectedTab(id)}
          allowedSources={allowedTargets}
          fluid={false}
        />
      </Stack>
      {selectedTab === ProfileSource.NamedResource && (
        <ReassignContentNamedResources
          onTargetProfileChange={onTargetProfileChange}
          rowSelectionState={rowSelectionState}
        />
      )}
      {selectedTab === ProfileSource.Matches && !!activities[0] && (
        <FiltersV2StoreContextProvider name={FiltersV2StorePage.ActivityShortlistPage}>
          <ReassignContentMatches
            sourceProfileIds={sourceProfileIds}
            activity={activities[0]}
            onTargetProfileChange={onTargetProfileChange}
            rowSelectionState={rowSelectionState}
            onNamedResourcesClick={
              isNamedResourceTabVisible ? () => setSelectedTab(ProfileSource.NamedResource) : undefined
            }
          />
        </FiltersV2StoreContextProvider>
      )}
    </Stack>
  );
};
